import React, { useState, useEffect } from 'react';
import blockchainService from '../../services/blockchainService';
import {
  Server,
  Plus,
  Settings,
  Activity,
  Zap,
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Edit,
  Trash2,
  Play,
  Pause,
  RotateCcw,
  Download,
  Upload,
  Cpu,
  HardDrive,
  Wifi,
  DollarSign,
  Calendar,
  Users,
  BarChart3,
  TrendingUp,
  Save,
  X,
  Info,
  User,
  Star,
  Router,
  Gauge,
  Wallet,
  Target
} from 'lucide-react';

// 타입 선언
declare global {
  interface Window {
    ethereum?: {
      request: (args: { method: string; params?: any[] }) => Promise<any>;
      on: (event: string, callback: (...args: any[]) => void) => void;
      removeListener: (event: string, callback: (...args: any[]) => void) => void;
      isMetaMask?: boolean;
    };
  }
}

interface Node {
  id: string;
  name: string;
  type: 'validator' | 'rpc' | 'archive' | 'light';
  status: 'running' | 'stopped' | 'error' | 'maintenance';
  region: string;
  specs: {
    cpu: string;
    ram: string;
    storage: string;
    bandwidth: string;
  };
  performance: {
    uptime: number;
    latency: number;
    throughput: number;
    syncStatus: number;
    cpuUsage?: number;
    memoryUsage?: number;
    diskUsage?: number;
    networkUsage?: number;
  };
  subscription: {
    plan: 'basic' | 'standard' | 'premium';
    monthlyFee: number;
    startDate: string;
    nextBilling: string;
    autoRenew: boolean;
  };
  earnings?: {
    totalGasFees: number;
    dailyGasFees: number;
    weeklyGasFees: number;
    monthlyGasFees: number;
    stakingRewards: number;
    validatorRewards: number;
  };
  operatorInfo?: {
    name: string;
    email: string;
    walletAddress: string;
    reputation: number;
    totalStaked: number;
    validatorSince: string;
    location: string;
    website?: string;
  };
  stakingInfo?: {
    totalStaked: number;
    delegatedStake: number;
    commissionRate: number;
    validatorRank: number;
    slashingHistory: number;
    uptimePercentage: number;
  };
  createdAt: string;
  lastActivity: string;
}

interface NodeStats {
  totalNodes: number;
  runningNodes: number;
  totalRevenue: number;
  averageUptime: number;
}

interface NodeReservation {
  id: number;
  user_address: string;
  nickname: string;
  phone: string;
  product_id: number;
  amount: string;
  status: 'pending' | 'active' | 'cancelled' | 'paid' | 'hold' | 'failed';
  payment_status: 'pending' | 'paid' | 'refunded';
  node_id: string | null;
  requested_at: string;
  created_at: string;
}

const NodeSubscriptionPage: React.FC = () => {
  // 탭 관리
  const [activeTab, setActiveTab] = useState<'nodes' | 'reservations'>('nodes');
  
  // 운영 노드 관련
  const [nodes, setNodes] = useState<Node[]>([]);
  const [stats, setStats] = useState<NodeStats>({
    totalNodes: 0,
    runningNodes: 0,
    totalRevenue: 0,
    averageUptime: 0
  });
  
  // 예약 현황 관련
  const [reservations, setReservations] = useState<NodeReservation[]>([]);
  const [selectedReservation, setSelectedReservation] = useState<NodeReservation | null>(null);
  const [showReservationCreateModal, setShowReservationCreateModal] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [creationMode, setCreationMode] = useState<'simple' | 'advanced'>('simple');
  const [newNode, setNewNode] = useState({
    name: '',
    type: 'validator' as 'validator' | 'rpc' | 'archive' | 'light',
    region: 'us-east-1',
    plan: 'standard' as 'basic' | 'standard' | 'premium' | 'custom',
    customPrice: 60000,
    customSpecs: {
      cpu: '8 vCPU',
      ram: '32 GB',
      storage: '1 TB SSD',
      bandwidth: '10 Gbps'
    },
    discountCode: '',
    discountRate: 0,
    gasSettings: {
      baseGas: 25,
      feeRate: 5,
      dynamicEnabled: true
    }
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR');
  };

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes}분 전`;
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)}시간 전`;
    } else {
      return `${Math.floor(diffInMinutes / 1440)}일 전`;
    }
  };

  useEffect(() => {
    if (activeTab === 'nodes') {
      loadRunningNodes();
    } else {
      loadReservations();
    }
    const interval = setInterval(() => {
      if (activeTab === 'nodes') {
        loadRunningNodes();
      } else {
        loadReservations();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [activeTab]);

  // 운영 중인 노드 로드 (ValidatorsPage API 사용)
  const loadRunningNodes = async () => {
    try {
      setLoading(true);
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
      const response = await fetch(`${apiBaseUrl}/nodes/status/all`);
      
      if (response.ok) {
        const data = await response.json();
        const rawNodes = data.nodes || [];
        
        // 실제 노드 데이터를 Node 인터페이스로 변환
        const formattedNodes: Node[] = rawNodes.map((node: any) => {
          const isValidator = node.type === 'validator';
          const isArchive = node.type === 'archivenode';
          
          return {
            id: node.nodeId || node.name || 'unknown',
            name: isValidator 
              ? `Validator ${node.nodeId?.replace('validator-', '') || ''}` 
              : isArchive 
                ? `Archive Node ${node.nodeId?.replace('archivenode-', '') || ''}`
                : `Full Node ${node.nodeId?.replace('fullnode-', '') || ''}`,
            type: isValidator ? 'validator' : isArchive ? 'archive' : 'rpc',
            status: node.status === 'active' ? 'running' : 'stopped',
            region: node.location || 'Seoul, South Korea',
            specs: {
              cpu: '8 vCPU',
              ram: '32 GB',
              storage: '1 TB SSD',
              bandwidth: '10 Gbps'
            },
            performance: {
              uptime: node.uptime || (node.status === 'active' ? 99.5 : 0),
              latency: 50,
              throughput: 1000,
              syncStatus: node.syncStatus || (node.status === 'active' ? 100 : 0),
              cpuUsage: 0,
              memoryUsage: 0,
              diskUsage: 0,
              networkUsage: 0
            },
            subscription: {
              plan: 'standard',
              monthlyFee: 60000,
              startDate: new Date().toISOString(),
              nextBilling: new Date().toISOString(),
              autoRenew: true
            },
            createdAt: new Date().toISOString(),
            lastActivity: new Date().toISOString()
          };
        });
        
        setNodes(formattedNodes);
        
        // 통계 계산
        const activeNodes = formattedNodes.filter(n => n.status === 'running');
        const averageUptime = activeNodes.length > 0 
          ? activeNodes.reduce((sum, n) => sum + n.performance.uptime, 0) / activeNodes.length 
          : 0;
        
        setStats({
          totalNodes: formattedNodes.length,
          runningNodes: activeNodes.length,
          totalRevenue: 0,
          averageUptime: averageUptime
        });
        
        setLoading(false);
      } else {
        setNodes([]);
        setStats({
          totalNodes: 0,
          runningNodes: 0,
          totalRevenue: 0,
          averageUptime: 0
        });
        setLoading(false);
      }
    } catch (error) {
      console.error('운영 노드 로드 실패:', error);
      setNodes([]);
      setLoading(false);
    }
  };

  // 예약 현황 로드
  const loadReservations = async () => {
    try {
      setLoading(true);
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
      const response = await fetch(`${apiBaseUrl}/admin/nodes/list`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setReservations(data.data);
        } else {
          setReservations([]);
        }
      } else {
        setReservations([]);
      }
    } catch (error) {
      console.error('예약 목록 로드 실패:', error);
      setReservations([]);
    } finally {
      setLoading(false);
    }
  };

  // 예약에서 노드 생성
  const handleCreateNodeFromReservation = async (nodeType: string, nodeName: string) => {
    if (!selectedReservation) return;

    try {
      alert('노드 생성을 시작합니다. 약 30초 소요됩니다...');
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
      const token = localStorage.getItem('voltron-admin-token') || localStorage.getItem('voltron-jwt-token');
      
      const response = await fetch(`${apiBaseUrl}/nodes/admin/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          reservationId: selectedReservation.id,
          nodeType: nodeType,
          nodeName: nodeName
        })
      });

      const result = await response.json();
      
      if (result.success) {
        alert(`✅ 노드 생성 성공!\n\n노드 ID: ${result.nodeId || result.data?.nodeId}\nRPC: ${result.rpcUrl || result.data?.rpcUrl}\nP2P Port: ${result.p2pPort || result.data?.p2pPort}\n\n사용자 앱에서 "내 노드"에 표시됩니다.`);
        loadReservations();
        loadRunningNodes(); // 운영 노드 목록도 새로고침
        setShowReservationCreateModal(false);
        setSelectedReservation(null);
      } else {
        const errorMsg = result.details ? `${result.error}\n\n상세: ${result.details}` : result.error;
        alert(`❌ 노드 생성 실패: ${errorMsg}`);
        console.error('노드 생성 실패 상세:', result);
      }
    } catch (error: any) {
      console.error('노드 생성 오류:', error);
      alert(`❌ 노드 생성 중 오류가 발생했습니다: ${error.message || '알 수 없는 오류'}`);
    }
  };

  // 예약 상태 변경
  const handleReservationStatusChange = async (reservationId: number, newStatus: string) => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
      const response = await fetch(`${apiBaseUrl}/admin/nodes/update-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reservationId,
          status: newStatus
        })
      });

      const result = await response.json();
      
      if (result.success) {
        alert('상태 변경 완료!');
        loadReservations();
      } else {
        alert(`상태 변경 실패: ${result.error}`);
      }
    } catch (error) {
      console.error('상태 변경 오류:', error);
      alert('상태 변경 중 오류가 발생했습니다.');
    }
  };

  // 관리자 강제 노드 생성
  const handleAdminCreateNode = async () => {
    if (!newNode.type) {
      alert('노드 타입을 선택해주세요.');
      return;
    }

    try {
      setLoading(true);
      alert('노드 생성을 시작합니다. 약 30초 소요됩니다...');
      
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
      const token = localStorage.getItem('voltron-admin-token') || localStorage.getItem('voltron-jwt-token');
      
      const response = await fetch(`${apiBaseUrl}/nodes/admin/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          nodeType: newNode.type === 'validator' ? 'validator' : newNode.type === 'archive' ? 'archive' : 'full',
          nodeName: newNode.name || undefined
        })
      });

      const result = await response.json();
      
      if (result.success) {
        const nodeId = result.nodeId || result.data?.nodeId;
        const nodeType = result.nodeType || result.data?.nodeType;
        const rpcUrl = result.rpcUrl || result.data?.rpcUrl;
        const p2pPort = result.p2pPort || result.data?.p2pPort;
        
        alert(`✅ 노드 생성 성공!\n\n노드 ID: ${nodeId}\n노드 타입: ${nodeType}\nRPC: ${rpcUrl}\nP2P Port: ${p2pPort}\n\nValidators 페이지에 추가되었습니다.`);
        setShowCreateModal(false);
        setNewNode({
          name: '',
          type: 'validator',
          region: 'us-east-1',
          plan: 'standard',
          customPrice: 60000,
          customSpecs: {
            cpu: '8 vCPU',
            ram: '32 GB',
            storage: '1 TB SSD',
            bandwidth: '10 Gbps'
          },
          discountCode: '',
          discountRate: 0,
          gasSettings: {
            baseGas: 25,
            feeRate: 5,
            dynamicEnabled: true
          }
        });
        // 노드 목록 새로고침
        await loadRunningNodes();
      } else {
        const errorMsg = result.details ? `${result.error}\n\n상세: ${result.details}` : result.error;
        alert(`❌ 노드 생성 실패: ${errorMsg}`);
        console.error('노드 생성 실패 상세:', result);
      }
    } catch (error: any) {
      console.error('노드 생성 오류:', error);
      alert(`❌ 노드 생성 중 오류가 발생했습니다: ${error.message || '알 수 없는 오류'}`);
    } finally {
      setLoading(false);
    }
  };


  const handleNodeAction = async (nodeId: string, action: 'stop' | 'start') => {
    try {
      setLoading(true);
      
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
      const response = await fetch(`${apiBaseUrl}/nodes/${nodeId}/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        // 로컬 상태 업데이트
        setNodes(prevNodes => prevNodes.map(node => 
          node.id === nodeId 
            ? { ...node, status: action === 'start' ? 'running' : 'stopped' }
            : node
        ));
        
        // 통계 업데이트
        if (action === 'start') {
          setStats(prevStats => ({
            ...prevStats,
            runningNodes: prevStats.runningNodes + 1
          }));
        } else {
          setStats(prevStats => ({
            ...prevStats,
            runningNodes: Math.max(0, prevStats.runningNodes - 1)
          }));
        }
        
        alert(`노드가 성공적으로 ${action === 'start' ? '시작' : '중지'}되었습니다.`);
        
        // 노드 목록 새로고침
        await loadRunningNodes();
      } else {
        throw new Error(data.error || `노드 ${action === 'start' ? '시작' : '중지'}에 실패했습니다.`);
      }
    } catch (error) {
      console.error(`Node ${action} failed:`, error);
      alert(`노드 ${action === 'start' ? '시작' : '중지'}에 실패했습니다: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNode = async (nodeId: string) => {
    if (!window.confirm('정말로 이 노드를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
      return;
    }

    try {
      setLoading(true);
      
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
      const response = await fetch(`${apiBaseUrl}/nodes/delete/${nodeId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        // 로컬 상태에서 노드 제거
        setNodes(prevNodes => prevNodes.filter(node => node.id !== nodeId));
        
        // 통계 업데이트
        setStats(prevStats => ({
          ...prevStats,
          totalNodes: Math.max(0, prevStats.totalNodes - 1),
          runningNodes: Math.max(0, prevStats.runningNodes - 1)
        }));
        
        alert('노드가 성공적으로 삭제되었습니다.');
        
        // 노드 목록 새로고침
        await loadRunningNodes();
      } else {
        throw new Error(data.error || '노드 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('Node deletion failed:', error);
      alert(`노드 삭제에 실패했습니다: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running': return 'text-green-600 bg-green-100';
      case 'stopped': return 'text-red-600 bg-red-100';
      case 'maintenance': return 'text-yellow-600 bg-yellow-100';
      case 'error': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running': return <CheckCircle className="w-4 h-4" />;
      case 'stopped': return <XCircle className="w-4 h-4" />;
      case 'maintenance': return <AlertTriangle className="w-4 h-4" />;
      case 'error': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const filteredNodes = nodes.filter(node => {
    const matchesSearch = node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         node.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || node.status === statusFilter;
    const matchesType = typeFilter === 'all' || node.type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">노드 팩토리</h1>
          <p className="text-gray-600 mt-1">Voltron 네트워크 노드 생성 및 관리 시스템</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>노드 생성</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('nodes')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'nodes'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              운영 노드
            </button>
            <button
              onClick={() => setActiveTab('reservations')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'reservations'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              예약 현황
            </button>
          </nav>
        </div>
      </div>

      {/* Stats Cards - 운영 노드 탭에서만 표시 */}
      {activeTab === 'nodes' && (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">총 노드</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalNodes}</p>
            </div>
            <div className="p-2 bg-blue-100 rounded-lg">
              <Server className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">실행 중</p>
              <p className="text-2xl font-bold text-green-600">{stats.runningNodes}</p>
            </div>
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">월 수익</p>
              <p className="text-2xl font-bold text-orange-600">${stats.totalRevenue}</p>
            </div>
            <div className="p-2 bg-orange-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">평균 가동률</p>
              <p className="text-2xl font-bold text-purple-600">{stats.averageUptime.toFixed(1)}%</p>
            </div>
            <div className="p-2 bg-purple-100 rounded-lg">
              <Activity className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Filters - 운영 노드 탭에서만 표시 */}
      {activeTab === 'nodes' && (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="노드 이름 또는 ID로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          >
            <option value="all">모든 상태</option>
            <option value="running">실행 중</option>
            <option value="stopped">중지됨</option>
            <option value="maintenance">유지보수</option>
            <option value="error">오류</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          >
            <option value="all">모든 타입</option>
            <option value="validator">검증자</option>
            <option value="rpc">RPC</option>
            <option value="archive">아카이브</option>
            <option value="light">라이트</option>
          </select>
        </div>
      </div>
      )}

      {/* Nodes Grid - 운영 노드 탭에서만 표시 */}
      {activeTab === 'nodes' && (
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">활성 노드</h3>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="text-gray-500">로딩 중...</div>
            </div>
          ) : filteredNodes.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-500">노드가 없습니다</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredNodes.map((node) => (
                <div key={node.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                  {/* 노드 헤더 */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
                        <Server className="h-6 w-6 text-orange-600" />
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">{node.name}</h4>
                        <p className="text-sm text-gray-500">{node.type.toUpperCase()} • {node.region}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(node.status)}
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(node.status)}`}>
                        {node.status === 'running' ? '활성' : node.status === 'stopped' ? '중지' : node.status === 'maintenance' ? '유지보수' : '오류'}
                      </span>
                    </div>
                  </div>

                  {/* 수익 정보 */}
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 mb-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-green-600">${node.earnings?.dailyGasFees || 55}</p>
                        <p className="text-xs text-gray-500">일일 수익</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-blue-600">${node.subscription.monthlyFee}</p>
                        <p className="text-xs text-gray-500">월 요금</p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">가동률:</span>
                        <span className="font-medium text-green-600">{node.performance.uptime}%</span>
                      </div>
                      <div className="flex justify-between text-sm mt-1">
                        <span className="text-gray-600">지연시간:</span>
                        <span className="font-medium text-gray-900">{node.performance.latency}ms</span>
                      </div>
                    </div>
                  </div>

                  {/* 액션 버튼 */}
                  <div className="flex items-center justify-between">
                    <button 
                      onClick={() => {
                        setSelectedNode(node);
                        setShowDetailModal(true);
                      }}
                      className="flex items-center space-x-2 px-3 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                      <span>상세보기</span>
                    </button>
                    <div className="flex items-center space-x-2">
                      <button 
                        onClick={() => {
                           setSelectedNode(node);
                           setNewNode({
                             name: node.name,
                             type: node.type,
                             region: node.region,
                             plan: node.subscription.plan,
                             customPrice: node.subscription.monthlyFee,
                             customSpecs: node.specs,
                             discountCode: '',
                             discountRate: 0,
                             gasSettings: {
                               baseGas: 25,
                               feeRate: 5,
                               dynamicEnabled: true
                             }
                           });
                           setShowCreateModal(true);
                         }}
                        className="flex items-center space-x-1 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                        <span>수정</span>
                      </button>
                      {node.status === 'running' ? (
                        <button
                          onClick={() => handleNodeAction(node.id, 'stop')}
                          className="flex items-center space-x-1 px-3 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
                        >
                          <Pause className="w-4 h-4" />
                          <span>중지</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => handleNodeAction(node.id, 'start')}
                          className="flex items-center space-x-1 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                        >
                          <Play className="w-4 h-4" />
                          <span>시작</span>
                        </button>
                      )}
                      <button 
                        onClick={() => handleDeleteNode(node.id)}
                        className="flex items-center space-x-1 px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>삭제</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {/* 예약 현황 탭 */}
      {activeTab === 'reservations' && (
        <div className="space-y-6">
          {/* 헤더 */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">노드 예약 현황</h2>
              <p className="text-sm text-gray-600 mt-1">
                총 {reservations.length}건의 예약 (결제완료: {reservations.filter(r => r.payment_status === 'paid').length}건)
              </p>
            </div>
            <button
              onClick={() => loadReservations()}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm"
            >
              새로고침
            </button>
          </div>

          {/* 테이블 */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">닉네임</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">전화번호</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">예약일</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">상태</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">결제</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">노드 ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">액션</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reservations.map((reservation) => (
                  <tr key={reservation.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">#{reservation.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{reservation.nickname}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{reservation.phone}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {new Date(reservation.requested_at || reservation.created_at).toLocaleDateString('ko-KR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {reservation.payment_status === 'paid' && reservation.status === 'active' ? (
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">활성</span>
                      ) : reservation.payment_status === 'paid' && reservation.status === 'pending' ? (
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">결제완료</span>
                      ) : reservation.status === 'cancelled' ? (
                        <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium">취소</span>
                      ) : (
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-medium">대기중</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        reservation.payment_status === 'paid' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {reservation.payment_status === 'paid' ? '완료' : '미결제'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-mono">
                      {reservation.node_id || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                      {/* 결제 완료이고 노드가 아직 생성 안된 경우 (pending 또는 paid 상태) */}
                      {reservation.payment_status === 'paid' && 
                       (reservation.status === 'pending' || reservation.status === 'paid') && 
                       !reservation.node_id && (
                        <>
                          <button
                            onClick={() => {
                              setSelectedReservation(reservation);
                              setShowReservationCreateModal(true);
                            }}
                            className="text-green-600 hover:text-green-900 font-medium"
                            title="노드 생성"
                          >
                            생성
                          </button>
                          <button
                            onClick={() => handleReservationStatusChange(reservation.id, 'hold')}
                            className="text-yellow-600 hover:text-yellow-900 font-medium"
                            title="보류"
                          >
                            보류
                          </button>
                          <button
                            onClick={() => handleReservationStatusChange(reservation.id, 'cancelled')}
                            className="text-red-600 hover:text-red-900 font-medium"
                            title="취소"
                          >
                            취소
                          </button>
                        </>
                      )}
                      {/* 무료 예약이고 노드가 아직 생성 안된 경우 */}
                      {reservation.payment_status === 'pending' && 
                       reservation.status === 'pending' && 
                       !reservation.node_id && (
                        <>
                          <button
                            onClick={() => {
                              setSelectedReservation(reservation);
                              setShowReservationCreateModal(true);
                            }}
                            className="text-green-600 hover:text-green-900 font-medium"
                            title="노드 생성"
                          >
                            생성
                          </button>
                          <button
                            onClick={() => handleReservationStatusChange(reservation.id, 'cancelled')}
                            className="text-red-600 hover:text-red-900 font-medium"
                            title="취소"
                          >
                            취소
                          </button>
                        </>
                      )}
                      {/* 노드가 이미 생성된 경우 */}
                      {reservation.status === 'active' && reservation.node_id && (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {reservations.length === 0 && (
              <div className="text-center py-12">
                <Server className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">예약 내역이 없습니다.</p>
              </div>
            )}
          </div>

          {/* 예약 노드 생성 모달 */}
          {showReservationCreateModal && selectedReservation && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 max-w-md w-full">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">노드 생성</h3>
                  <button 
                    onClick={() => {
                      setShowReservationCreateModal(false);
                      setSelectedReservation(null);
                    }} 
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">예약 정보</label>
                    <div className="bg-gray-50 rounded p-3 space-y-1 text-sm">
                      <p><span className="font-medium">닉네임:</span> {selectedReservation.nickname}</p>
                      <p><span className="font-medium">전화번호:</span> {selectedReservation.phone}</p>
                      <p><span className="font-medium">금액:</span> {selectedReservation.amount} VTTN</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">노드 타입</label>
                    <select
                      id="reservation-node-type"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      defaultValue="full"
                    >
                      <option value="full">Full Node</option>
                      <option value="validator">Validator Node</option>
                      <option value="archive">Archive Node</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">노드 이름</label>
                    <input
                      type="text"
                      id="reservation-node-name"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      placeholder="node-name"
                      defaultValue={`node-${Date.now()}`}
                    />
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      onClick={() => {
                        const nodeType = (document.getElementById('reservation-node-type') as HTMLSelectElement)?.value || 'full';
                        const nodeName = (document.getElementById('reservation-node-name') as HTMLInputElement)?.value || `node-${Date.now()}`;
                        handleCreateNodeFromReservation(nodeType, nodeName);
                      }}
                      className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium"
                    >
                      노드 생성
                    </button>
                    <button
                      onClick={() => {
                        setShowReservationCreateModal(false);
                        setSelectedReservation(null);
                      }}
                      className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium"
                    >
                      취소
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 노드 상세 모달 - 개선된 버전 */}
      {showDetailModal && selectedNode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
            {/* 헤더 */}
            <div className="bg-white border-b border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Server className="w-6 h-6 text-orange-500" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{selectedNode.name}</h3>
                    <p className="text-gray-500 text-sm">노드 ID: {selectedNode.id}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className={`px-2 py-1 rounded text-xs font-medium ${
                    selectedNode.status === 'running' ? 'bg-green-100 text-green-800' :
                    selectedNode.status === 'stopped' ? 'bg-red-100 text-red-800' :
                    selectedNode.status === 'maintenance' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedNode.status === 'running' ? '실행 중' :
                     selectedNode.status === 'stopped' ? '중지됨' :
                     selectedNode.status === 'maintenance' ? '유지보수' : '오류'}
                  </div>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* 콘텐츠 */}
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
              {/* 상태 카드들 */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-gray-50 border border-gray-200 p-3 rounded-lg text-center">
                  <Gauge className="w-6 h-6 mx-auto mb-1 text-blue-500" />
                  <p className="text-xs text-gray-600">가동률</p>
                  <p className="text-lg font-bold text-gray-900">{selectedNode.performance.uptime}%</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 p-3 rounded-lg text-center">
                  <DollarSign className="w-6 h-6 mx-auto mb-1 text-green-500" />
                  <p className="text-xs text-gray-600">일일 수익</p>
                  <p className="text-lg font-bold text-gray-900">${selectedNode.earnings?.dailyGasFees || 45.67}</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 p-3 rounded-lg text-center">
                  <Shield className="w-6 h-6 mx-auto mb-1 text-purple-500" />
                  <p className="text-xs text-gray-600">스테이킹</p>
                  <p className="text-lg font-bold text-gray-900">{selectedNode.stakingInfo?.totalStaked || 120000} VTTN</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 p-3 rounded-lg text-center">
                  <Star className="w-6 h-6 mx-auto mb-1 text-yellow-500" />
                  <p className="text-xs text-gray-600">검증자 순위</p>
                  <p className="text-lg font-bold text-gray-900">#{selectedNode.stakingInfo?.validatorRank || 15}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* 기본 정보 */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <Server className="w-4 h-4 text-orange-500" />
                    <h4 className="text-sm font-semibold text-gray-900">기본 정보</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">노드 타입:</span>
                      <span className="font-medium text-gray-900">{selectedNode.type.toUpperCase()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">지역:</span>
                      <span className="font-medium text-gray-900">{selectedNode.region}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">생성일:</span>
                      <span className="font-medium text-gray-900">{formatDate(selectedNode.createdAt)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">마지막 활동:</span>
                      <span className="font-medium text-gray-900">{formatDate(selectedNode.lastActivity)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">월 요금:</span>
                      <span className="font-medium text-orange-600">${selectedNode.subscription.monthlyFee}</span>
                    </div>
                  </div>
                </div>

                {/* 블록체인 연동 정보 */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <Activity className="w-4 h-4 text-blue-500" />
                    <h4 className="text-sm font-semibold text-gray-900">블록체인 연동</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">네트워크:</span>
                      <span className="font-medium text-blue-600">Voltron Mainnet</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">체인 ID:</span>
                      <span className="font-medium text-gray-900">88889</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">최신 블록:</span>
                      <span className="font-medium text-gray-900">#2,847,123</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">동기화 상태:</span>
                      <span className="font-medium text-green-600">100% 동기화됨</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">피어 연결:</span>
                      <span className="font-medium text-gray-900">47개 노드</span>
                    </div>
                  </div>
                </div>

                {/* 블록 생성 히스토리 */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <BarChart3 className="w-4 h-4 text-purple-500" />
                    <h4 className="text-sm font-semibold text-gray-900">블록 생성 히스토리</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">오늘 생성 블록:</span>
                      <span className="font-medium text-gray-900">156개</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">이번 주 총 블록:</span>
                      <span className="font-medium text-gray-900">1,089개</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">평균 블록 시간:</span>
                      <span className="font-medium text-gray-900">2.1초</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">마지막 블록:</span>
                      <span className="font-medium text-blue-600">#2,847,123</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">블록 보상:</span>
                      <span className="font-medium text-green-600">2.5 VTTN</span>
                    </div>
                  </div>
                </div>

                {/* 수익 정보 */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <DollarSign className="w-4 h-4 text-green-500" />
                    <h4 className="text-sm font-semibold text-gray-900">수익 정보</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">총 가스비 수익:</span>
                      <span className="font-medium text-green-600">${selectedNode.earnings?.totalGasFees || 1234.56}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">일일 가스비:</span>
                      <span className="font-medium text-gray-900">${selectedNode.earnings?.dailyGasFees || 45.67}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">스테이킹 보상:</span>
                      <span className="font-medium text-gray-900">${selectedNode.earnings?.stakingRewards || 89.12}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">검증자 보상:</span>
                      <span className="font-medium text-gray-900">${selectedNode.earnings?.validatorRewards || 156.78}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">월간 예상 수익:</span>
                      <span className="font-medium text-green-600">${selectedNode.earnings?.monthlyGasFees || 1370.10}</span>
                    </div>
                  </div>
                </div>

                {/* 성능 지표 */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <Cpu className="w-4 h-4 text-blue-500" />
                    <h4 className="text-sm font-semibold text-gray-900">성능 지표</h4>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-center">
                      <div className="relative w-12 h-12 mx-auto mb-1">
                        <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 36 36">
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#e5e7eb"
                            strokeWidth="3"
                          />
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#3b82f6"
                            strokeWidth="3"
                            strokeDasharray={`${selectedNode.performance.cpuUsage || 45}, 100`}
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xs font-medium text-gray-900">{selectedNode.performance.cpuUsage || 45}%</span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600">CPU</p>
                    </div>
                    <div className="text-center">
                      <div className="relative w-12 h-12 mx-auto mb-1">
                        <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 36 36">
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#e5e7eb"
                            strokeWidth="3"
                          />
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#f59e0b"
                            strokeWidth="3"
                            strokeDasharray={`${selectedNode.performance.memoryUsage || 67}, 100`}
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xs font-medium text-gray-900">{selectedNode.performance.memoryUsage || 67}%</span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600">메모리</p>
                    </div>
                    <div className="text-center">
                      <div className="relative w-12 h-12 mx-auto mb-1">
                        <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 36 36">
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#e5e7eb"
                            strokeWidth="3"
                          />
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#10b981"
                            strokeWidth="3"
                            strokeDasharray={`${selectedNode.performance.diskUsage || 34}, 100`}
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xs font-medium text-gray-900">{selectedNode.performance.diskUsage || 34}%</span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600">디스크</p>
                    </div>
                    <div className="text-center">
                      <div className="relative w-12 h-12 mx-auto mb-1">
                        <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 36 36">
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#e5e7eb"
                            strokeWidth="3"
                          />
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#ef4444"
                            strokeWidth="3"
                            strokeDasharray={`${selectedNode.performance.networkUsage || 78}, 100`}
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xs font-medium text-gray-900">{selectedNode.performance.networkUsage || 78}%</span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600">네트워크</p>
                    </div>
                  </div>
                </div>

                {/* 하드웨어 사양 */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <HardDrive className="w-4 h-4 text-gray-500" />
                    <h4 className="text-sm font-semibold text-gray-900">하드웨어 사양</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">CPU:</span>
                      <span className="font-medium text-gray-900">{selectedNode.specs.cpu}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">RAM:</span>
                      <span className="font-medium text-gray-900">{selectedNode.specs.ram}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">스토리지:</span>
                      <span className="font-medium text-gray-900">{selectedNode.specs.storage}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">대역폭:</span>
                      <span className="font-medium text-gray-900">{selectedNode.specs.bandwidth}</span>
                    </div>
                  </div>
                </div>

                {/* 최근 트랜잭션 */}
                <div className="bg-white border border-gray-200 rounded-lg p-4 col-span-2">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <TrendingUp className="w-4 h-4 text-indigo-500" />
                      <h4 className="text-sm font-semibold text-gray-900">최근 처리된 트랜잭션</h4>
                    </div>
                    <button className="text-xs text-blue-600 hover:text-blue-800">블록 페이지에서 보기</button>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs">
                      <span className="font-mono text-blue-600">0x8f2a...9b4c</span>
                      <span className="text-gray-600">2분 전</span>
                      <span className="font-medium text-green-600">+0.025 VTTN</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs">
                      <span className="font-mono text-blue-600">0x7e1b...8a3d</span>
                      <span className="text-gray-600">5분 전</span>
                      <span className="font-medium text-green-600">+0.031 VTTN</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs">
                      <span className="font-mono text-blue-600">0x6d0c...7f2e</span>
                      <span className="text-gray-600">8분 전</span>
                      <span className="font-medium text-green-600">+0.019 VTTN</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 푸터 */}
            <div className="bg-gray-50 px-4 py-3 flex justify-between items-center">
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                닫기
              </button>
              <div className="flex space-x-2">
                <button 
                  onClick={() => window.open('/blocks', '_blank')}
                  className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                >
                  블록 페이지 보기
                </button>
                <button 
                  onClick={() => window.open('/validators', '_blank')}
                  className="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                >
                  검증자 보기
                </button>
                <button 
                  onClick={() => navigator.clipboard.writeText(selectedNode.operatorInfo?.walletAddress || '0x742d35Cc6634C0532925a3b8D4C2C2C2C2C2C2C2')}
                  className="px-3 py-1 text-sm bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
                >
                  주소 복사
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 노드 생성 모달 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden">
            <div className="bg-white border-b border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Server className="w-6 h-6 text-orange-500" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">새 노드 생성</h3>
                    <p className="text-gray-500 text-sm">Voltron 네트워크 노드를 생성합니다</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="space-y-6">
                {/* 노드 기본 정보 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">노드 이름</label>
                    <input
                      type="text"
                      value={newNode.name}
                      onChange={(e) => setNewNode({...newNode, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="Voltron Validator 01"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">노드 타입</label>
                    <select
                      value={newNode.type}
                      onChange={(e) => setNewNode({...newNode, type: e.target.value as any})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="validator">Validator</option>
                      <option value="full">Full Node</option>
                      <option value="archive">Archive Node</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">지역</label>
                    <select
                      value={newNode.region}
                      onChange={(e) => setNewNode({...newNode, region: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="us-east-1">US East (N. Virginia)</option>
                      <option value="us-west-2">US West (Oregon)</option>
                      <option value="eu-west-1">Europe (Ireland)</option>
                      <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
                      <option value="ap-northeast-1">Asia Pacific (Tokyo)</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">요금제</label>
                    <select
                      value={newNode.plan}
                      onChange={(e) => setNewNode({...newNode, plan: e.target.value as any})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="basic">Basic - 60,000 USDT(VTTN)</option>
                      <option value="standard">Standard - 60,000 USDT(VTTN)</option>
                      <option value="premium">Premium - 60,000 USDT(VTTN)</option>
                      <option value="custom">직접 입력</option>
                    </select>
                    
                    {newNode.plan === 'custom' && (
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-gray-700 mb-2">사용자 정의 가격</label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            value={newNode.customPrice}
                            onChange={(e) => setNewNode({...newNode, customPrice: parseInt(e.target.value) || 0})}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            placeholder="60000"
                            min="1000"
                            max="1000000"
                          />
                          <span className="text-sm text-gray-600">USDT(VTTN)</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* 할인 코드 */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-yellow-800 mb-3">할인 코드 적용</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-yellow-700 mb-2">할인 코드</label>
                      <input
                        type="text"
                        value={newNode.discountCode}
                        onChange={(e) => setNewNode({...newNode, discountCode: e.target.value})}
                        className="w-full px-3 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                        placeholder="VOLTRON50, EARLY2024 등"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-yellow-700 mb-2">할인율</label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="range"
                          min="0"
                          max="50"
                          value={newNode.discountRate}
                          onChange={(e) => setNewNode({...newNode, discountRate: parseInt(e.target.value)})}
                          className="flex-1"
                        />
                        <span className="text-sm font-medium text-yellow-800">{newNode.discountRate}%</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 하드웨어 사양 */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-800 mb-3">하드웨어 사양</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CPU</label>
                      <input
                        type="text"
                        value={newNode.customSpecs.cpu}
                        onChange={(e) => setNewNode({...newNode, customSpecs: {...newNode.customSpecs, cpu: e.target.value}})}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        placeholder="8 vCPU"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">RAM</label>
                      <input
                        type="text"
                        value={newNode.customSpecs.ram}
                        onChange={(e) => setNewNode({...newNode, customSpecs: {...newNode.customSpecs, ram: e.target.value}})}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        placeholder="32 GB"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Storage</label>
                      <input
                        type="text"
                        value={newNode.customSpecs.storage}
                        onChange={(e) => setNewNode({...newNode, customSpecs: {...newNode.customSpecs, storage: e.target.value}})}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        placeholder="1 TB SSD"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bandwidth</label>
                      <input
                        type="text"
                        value={newNode.customSpecs.bandwidth}
                        onChange={(e) => setNewNode({...newNode, customSpecs: {...newNode.customSpecs, bandwidth: e.target.value}})}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        placeholder="10 Gbps"
                      />
                    </div>
                  </div>
                </div>

                {/* 가스비 설정 */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-blue-800 mb-3">가스비 설정</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-2">기본 가스비</label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={newNode.gasSettings.baseGas}
                          onChange={(e) => setNewNode({...newNode, gasSettings: {...newNode.gasSettings, baseGas: parseInt(e.target.value) || 25}})}
                          className="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="25"
                        />
                        <span className="text-sm text-blue-600">Gwei</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-2">수수료 비율</label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="range"
                          min="1"
                          max="10"
                          value={newNode.gasSettings.feeRate}
                          onChange={(e) => setNewNode({...newNode, gasSettings: {...newNode.gasSettings, feeRate: parseInt(e.target.value)}})}
                          className="flex-1"
                        />
                        <span className="text-sm font-medium text-blue-800">{newNode.gasSettings.feeRate}%</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-2">자동 조정</label>
                      <label className="flex items-center">
                        <input 
                          type="checkbox" 
                          className="mr-2" 
                          checked={newNode.gasSettings.dynamicEnabled}
                          onChange={(e) => setNewNode({...newNode, gasSettings: {...newNode.gasSettings, dynamicEnabled: e.target.checked}})}
                        />
                        <span className="text-sm text-blue-600">동적 가스비 활성화</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-between items-center">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                취소
              </button>
              <div className="flex space-x-3">
                <button
                  className="px-4 py-2 text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  미리보기
                </button>
                <button
                  onClick={handleAdminCreateNode}
                  disabled={loading}
                  className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors"
                >
                  {loading ? '생성 중...' : '노드 생성'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NodeSubscriptionPage;
export {};